'use client';

import React from 'react';

import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

/** Keeps CardContent framing consistent across floating, inline, and table offer cards. */
export const OFFER_CARD_CONTENT_SX = {
  p: 0,
  display: 'flex',
  '&:last-child': {
    pb: 0,
  },
} as const;

/** Defines owner-provided regions wrapped by the presentation's shared card frame. */
interface TableOfferCardPresentationProps {
  /** Owner-created image or placeholder displayed inside the shared image frame. */
  image: React.ReactNode;
  /** Owner-created controls displayed over the image region. */
  overlay?: React.ReactNode;
  /** Content displayed inside the shared title typography. */
  title: React.ReactNode;
  /** Optional content displayed inside the shared comments typography. */
  comments?: React.ReactNode;
  /** Owner-created content displayed after the comments region. */
  footer?: React.ReactNode;
}

/** Provides the shared visual structure for internal and external table offer cards. */
export const TableOfferCardPresentation: React.FC<TableOfferCardPresentationProps> = ({
  image,
  overlay,
  title,
  comments,
  footer,
}) => {
  const theme = useTheme();

  return (
    <CardContent sx={OFFER_CARD_CONTENT_SX}>
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: 120,
          minHeight: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--mui-palette-background-paper)',
          [theme.breakpoints.down('sm')]: {
            width: 84,
            minHeight: 84,
          },
        }}
      >
        {image}
        {overlay}
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          padding: theme.spacing(1.5, 2),
          gap: theme.spacing(1),
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 1.5),
            gap: theme.spacing(0.75),
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: '0.9rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            wordBreak: 'break-word',
            [theme.breakpoints.down('sm')]: {
              fontSize: '0.8rem',
            },
          }}
        >
          {title}
        </Typography>
        {comments != null && comments !== false && comments !== '' && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--mui-palette-text-secondary)',
              lineHeight: 1.4,
              fontSize: '0.85rem',
              flex: 1,
              [theme.breakpoints.down('sm')]: {
                fontSize: '0.8rem',
              },
            }}
          >
            {comments}
          </Typography>
        )}
        {footer}
      </Box>
    </CardContent>
  );
};
